<template>
  <div class="UserEditModalProfile">
    <div class="UserEditModalProfile__value">
      <ValidationProvider v-slot="{ errors }" :rules="textInputRules" immediate>
        <LLInputText
          v-model.trim="localUser.firstName"
          :placeholder="$t('placeholder_first_name_text_box')"
          :errors="errors"
          data-e2e="firstNameFirstVisit"
        >
          <template #caption>{{ $t('placeholder_first_name') }}</template>
        </LLInputText>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value">
      <ValidationProvider v-slot="{ errors }" :rules="textInputRules" immediate>
        <LLInputText
          v-model.trim="localUser.lastName"
          :placeholder="$t('placeholder_last_name_text_box')"
          :errors="errors"
          data-e2e="lastNameFirstVisit"
        >
          <template #caption>{{ $t('placeholder_last_name') }}</template>
        </LLInputText>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <PhoneVerificationNote v-if="phoneVerification && !phoneIsVerified" />
      <div class="UserEditModalProfile__phone">
        <div v-if="phoneIsVerified" class="UserEditModalProfile__locked-phone" data-e2e="phoneLocked">
          <div class="UserEditModalProfile__locked-caption">{{ $t('placeholder_mobile_phone') }}</div>
          <div>{{ localUser.phone }}</div>
        </div>
        <LLPhoneInput
          v-else
          v-model="localUser.phone"
          :required="!!textInputRules"
          :errors="phoneError ? [phoneError] : []"
          data-e2e="phoneFirstVisit"
        >
          <template #caption>{{ $t('placeholder_mobile_phone') }}</template>
        </LLPhoneInput>
        <PhoneVerification
          v-if="phoneVerification"
          :phone="localUser.phone || ''"
          :study-id="phoneVerification.studyId"
          :verified="phoneIsVerified"
          @verified="onPhoneVerified"
          @edit="onPhoneEdit"
        />
      </div>
    </div>
    <div class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <ValidationProvider
        v-slot="{ errors }"
        name="sms-consent-agreement"
        :rules="smsConsentRequired ? 'required' : undefined"
        immediate
      >
        <LLCheckbox
          v-model="localUser.smsConsentAccepted"
          class="UserEditModalProfile__checkbox UserEditModalProfile__sms-consent-checkbox"
          :errors="errors"
          data-e2e="sms_consent_agreement_checkbox"
        >
          <span class="UserEditModalProfile__sms-consent-checkbox-text">
            {{ $t('placeholder_sms_consent_agreement') }}
          </span>
          <span v-if="smsConsentRequired" class="UserEditModalProfile__sms-consent-checkbox-required">*</span>
        </LLCheckbox>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value">
      <ValidationProvider v-slot="{ errors }" :rules="textInputRules" immediate>
        <LLSelectInputNew
          v-model="localUser.gender"
          :errors="errors"
          :key-fn="(value) => value.value"
          error-space
          :caption="(gender) => $t(`gender_${gender.label}`)"
          :reduce="(gender) => gender.value"
          :values="genders"
          :placeholder="$t('placeholder_gender')"
          class="UserEditModalProfile__input"
          data-e2e="genderFirstVisit"
        >
          <template #caption>{{ $t('placeholder_gender') }}</template>
        </LLSelectInputNew>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value" data-e2e="yearsFirstVisit">
      <ValidationProvider v-slot="{ errors }" :rules="textInputRules" immediate>
        <LLYearInput v-model="localUser.birthday" :errors="errors">
          <template #caption>{{ $t('placeholder_date_of_birth') }}</template>
        </LLYearInput>
        <div class="UserEditModalProfile__birthday-notice">
          {{ $t('your_birthday_won_t_be_shown_publicity') }}
        </div>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value">
      <ValidationProvider v-slot="{ errors }" :rules="textInputRules" immediate>
        <LLSelectInputNew
          v-model="language"
          :values="availableLocales"
          :caption="(l) => l.label"
          rules="required"
          data-e2e="userLanguageInput"
          :errors="errors"
          :key-fn="(value) => value.value"
          error-space
          :reduce="(l) => l.code"
          :placeholder="$t('placeholder_preferred_language')"
        >
          <template #caption>{{ $t('placeholder_preferred_language') }}</template>
        </LLSelectInputNew>
      </ValidationProvider>
    </div>
    <div class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <LLLocationInput
        :validation-rules="textInputRules"
        :placeholder="$t('placeholder_start-typing')"
        :location="localUser.location"
        data-e2e="locationFirstVisit"
        @update="updateLocation"
      >
        <template #caption>
          {{ $t('placeholder_location') }}
        </template>
      </LLLocationInput>
      <div class="UserEditModalProfile__birthday-notice">
        {{ $t('location_input_notice') }}
      </div>
    </div>
    <div v-if="!hideProfileDetails" class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <LLInputText
        v-model.trim="localUser.occupation"
        :placeholder="$t('placeholder_occupation')"
        data-e2e="occupationInput"
      >
        <template #caption>{{ $t('placeholder_occupation') }}</template>
      </LLInputText>
    </div>
    <div v-if="!hideProfileDetails" class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <LLTextarea
        v-model="localUser.aboutMe"
        size="compact"
        dynamic-size
        plain-text
        hide-menu-bar
        hide-emoji
        :max-height="200"
        :max-length="200"
        :placeholder="$t('placeholder_about_me_hint')"
        data-e2e="aboutMeInput"
      >
        <template #caption>{{ $t('placeholder_about_me') }}</template>
        <template #bottom-content>
          <div class="UserEditModalProfile__counter">{{ (localUser.aboutMe || '').length }} / 200</div>
        </template>
      </LLTextarea>
      <div class="UserEditModalProfile__scope">{{ $t('profile_edit_scope') }}</div>
    </div>
    <div class="UserEditModalProfile__value UserEditModalProfile__value_full">
      <ValidationProvider
        v-slot="{ errors }"
        name="ai-warning-agreement"
        :rules="aiWarningRequired ? 'required' : undefined"
        immediate
      >
        <LLCheckbox
          v-model="localUser.aiWarningAccepted"
          class="UserEditModalProfile__checkbox UserEditModalProfile__ai-warning-checkbox"
          :errors="errors"
          :disabled="aiWarningLocked"
          data-e2e="ai_warning_agreement_checkbox"
        >
          <span class="UserEditModalProfile__ai-warning-checkbox-text">
            {{ $t('placeholder_ai-warning-text') }}
          </span>
          <span v-if="aiWarningRequired" class="UserEditModalProfile__ai-warning-checkbox-required">*</span>
          <span v-if="!aiWarningLocked" class="UserEditModalProfile__ai-warning-checkbox-notice">
            {{ $t('placeholder_ai-warning-notice') }}
          </span>
          <span v-if="aiWarningLocked" class="UserEditModalProfile__ai-warning-checkbox-notice">
            {{ $t('placeholder_ai-warning-agreed-notice') }}
          </span>
        </LLCheckbox>
      </ValidationProvider>
      <LLCheckbox
        v-model="pushSubscribed"
        class="UserEditModalProfile__checkbox UserEditModalProfile__push-checkbox"
        @change="onTogglePush"
      >
        <span class="UserEditModalProfile__push-checkbox-text">
          {{ $t('placeholder_push_notifications') || 'Enable push notifications' }}
        </span>
      </LLCheckbox>
    </div>
  </div>
</template>

<script>
import LLPhoneInput from '@/components/common/LLPhoneInput'
import PhoneVerification from '@/components/user/PhoneVerification'
import PhoneVerificationNote from '@/components/user/PhoneVerificationNote'
import { ProspectModel } from '@/models/prospect'
import LLLocationInput from '@/components/common/LLLocationInput'
import LLSelectInputNew from '@/components/common/LLSelectInputNew'
import LLYearInput from '@/components/common/LLYearInput'
import LLInputText from '@/components/common/LLInput/LLInputText'
import LLTextarea from '@/components/common/LLTextarea.vue'
import LLCheckbox from '@/components/common/ui-components/LLCheckbox'
import languageLabels from '@/common/languages.json'
import { enableNotifications } from '@/utils/notificationGate'
import { unsubscribeFromPush } from '@/utils/pushNotifications'
import notificationPromptService from '@/services/notificationPromptService'
export default {
  name: 'UserEditModalProfile',
  components: {
    LLCheckbox,
    LLInputText,
    LLTextarea,
    LLYearInput,
    LLSelectInputNew,
    LLLocationInput,
    LLPhoneInput,
    PhoneVerification,
    PhoneVerificationNote
  },
  props: {
    user: { type: ProspectModel, default: () => new ProspectModel() },
    smsConsentRequired: { type: Boolean, default: false },
    aiWarningRequired: { type: Boolean, default: false },
    aiWarningLocked: { type: Boolean, default: false },
    // The initial registration form must not collect Occupation / About me — those
    // are seeded later by the optional profile details page (LL-169).
    hideProfileDetails: { type: Boolean, default: false },
    // { studyId } when the study being entered requires a verified phone (#1709)
    phoneVerification: { type: Object, default: null },
    // Message of a failed save that is about the phone; shown under the phone field
    phoneError: { type: String, default: '' }
  },
  data() {
    return {
      localUser: null,
      pushSubscribed: false,
      pushToggling: false,
      verifiedPhone: null
    }
  },
  computed: {
    textInputRules() {
      if (this.localUser?.requireRegistration) {
        return 'required'
      } else {
        return null
      }
    },
    phoneIsVerified() {
      return !!this.phoneVerification && !!this.verifiedPhone && this.verifiedPhone === this.localUser?.phone
    },
    language: {
      get() {
        let value = ''
        if (!this.localUser?.language) {
          value = this.$i18n.locale
        } else {
          value = this.localUser?.language
        }
        return this.availableLocales.find((l) => l.code === value)?.code
      },
      set(locale) {
        locale = locale?.toLowerCase()
        this.localUser.language = locale
      }
    },
    genders() {
      return this.$store.getters.gendersArray
    },
    availableLocales() {
      return this.$i18n.availableLocales.map((l) => ({
        code: l,
        label: languageLabels?.[l]
      }))
    },
    currentGlobalLocale() {
      return this.$i18n.locale
    }
  },
  watch: {
    currentGlobalLocale() {
      if (this.localUser) {
        this.localUser.language = this.$i18n.locale
      }
    },
    user: {
      handler() {
        if (!this.$_.isEqual(this.user, this.localUser)) {
          this.localUser = new ProspectModel(this.user)
        }
        if (this.aiWarningLocked) {
          // The AI pledge is a condition of using the platform — it cannot be opted out of
          this.localUser.aiWarningAccepted = true
        }
        if (this.localUser?.firstName || this.localUser?.lastName) {
          this.localUser?.firstName.trim()
          this.localUser?.lastName.trim()
        }
      },
      immediate: true,
      deep: true
    },
    localUser: {
      handler() {
        this.$emit('update:user', new ProspectModel(this.localUser))
      },
      deep: true
    },
    phoneIsVerified: {
      handler(value) {
        this.$emit('phone-verified', value)
      },
      immediate: true
    },
    'localUser.pushNotificationsEnabled': {
      handler(val) {
        this.pushSubscribed = !!val
      }
    }
  },
  created() {
    this.pushSubscribed = this.localUser?.pushNotificationsEnabled ?? true
  },
  methods: {
    onPhoneVerified({ phone, prospectAccount }) {
      this.verifiedPhone = phone
      this.$emit('phone-verified-account', prospectAccount)
    },
    onPhoneEdit() {
      // 2.12: editing clears the verified mark and requires a new code
      this.verifiedPhone = null
    },
    refreshPushSubscription() {
      this.pushSubscribed = this.localUser?.pushNotificationsEnabled ?? true
    },
    showNotificationPrompt(params) {
      notificationPromptService.show(params, {
        onClose: this.refreshPushSubscription
      })
    },
    async onTogglePush(shouldEnable) {
      if (this.pushToggling) return
      this.pushToggling = true

      // Optimistic update — checkbox reacts immediately
      this.pushSubscribed = shouldEnable

      // Start the shared P2 gate synchronously so Safari keeps the user gesture
      // for Notification.requestPermission() (enableNotifications runs sync until
      // the browser prompt is shown). The account-level flag (B1) is persisted in
      // parallel. Uncheck is silent: no popups, no device unsubscribe (per decision
      // — only the account flag changes).
      let gateSucceeded = false

      const gatePromise = shouldEnable
        ? enableNotifications(this.$api, this.$store, {
            onIOSCoach: () => this.showNotificationPrompt({ force: true, forceIOS: true }),
            onUnsupported: () => this.showNotificationPrompt({ force: true, unsupported: true }),
            onAwaitPermission: () => this.showNotificationPrompt({ startAwaiting: true }),
            onSubscribed: () => {
              gateSucceeded = true
              this.$notify({
                group: 'main',
                type: 'success',
                title: this.$t('popups.local-notification-prompt.opted_in_toast')
              })
              // Close the awaiting helper if it was shown
              notificationPromptService.close()
            },
            onDenied: () => this.showNotificationPrompt({ force: true }),
            onFailed: () => {
              // Browser prompt dismissed without a decision — just close
              notificationPromptService.close()
            }
          })
        : Promise.resolve()

      try {
        await this.$store.dispatch('user/syncUser', { pushNotificationsEnabled: shouldEnable })

        if (!shouldEnable) return

        await gatePromise
      } catch (e) {
        // Revert to the server-side state on failure
        this.pushSubscribed = this.localUser?.pushNotificationsEnabled ?? true

        // Compensate: the gate runs in parallel with syncUser — if the user
        // granted permission (subscription created) but the account flag failed
        // to persist, undo the subscription so the UI matches the server state.
        if (shouldEnable) {
          try {
            await gatePromise
          } catch (gateErr) {
            // The gate already reported its own failure via its handlers
          }
          if (gateSucceeded) {
            try {
              await unsubscribeFromPush(this.$api)
            } catch (unsubErr) {
              console.error('Failed to compensate push subscription after syncUser failure:', unsubErr)
            }
          }
        }
      } finally {
        this.pushToggling = false
      }
    },
    updateGlobalLocation() {},
    updateLocation(location) {
      this.localUser.location = { ...location }
    }
  }
}
</script>

<style lang="scss" scoped>
.UserEditModalProfile__locked-phone {
  @apply mb-1;
}
.UserEditModalProfile__locked-caption {
  @apply mb-1 text-sm;
}
.UserEditModalProfile {
  $root: &;
  @apply grid grid-cols-2 items-start;
  @screen md-max {
    @apply grid-cols-1;
  }
  // The phone row spans the form so that the info note above the field can use the full width; the field itself
  // keeps the width of one column. The row takes the grid position the phone cell had, so the columns of the
  // fields after it do not change.
  &__phone {
    @apply w-1/2 pr-5;
    @screen md-max {
      @apply w-full pr-0;
    }
  }
  #{$root}__value {
    @apply pt-6;
    &:nth-child(1) {
      @apply pt-0;
    }
    &:nth-child(2) {
      @apply pt-0;
      @screen md-max {
        @apply pt-6;
      }
    }
    &:nth-child(2n) {
      @apply pl-5;
      @screen md-max {
        @apply pl-0;
      }
    }
    &:nth-child(2n-1) {
      @apply pr-5;
      @screen md-max {
        @apply pr-0;
      }
    }
    #{$root}__input {
      @apply w-full;
    }
    &_full {
      padding-right: 0 !important;
      padding-left: 0 !important;
      grid-column-start: 1;
      grid-column-end: 3;
      @screen md-max {
        grid-column-end: 2;
      }
    }
  }
  &__sms-consent-checkbox {
    @apply mt-4;
    &-required {
      @apply text-status-03-300 absolute ml-1;
      vertical-align: super;
    }
    &-text {
      @apply text-body-02 -mt-0.5;
    }
  }
  &__ai-warning-checkbox {
    @apply mt-4;
    &.ll-checkbox_disabled {
      .UserEditModalProfile__ai-warning-checkbox-text,
      .UserEditModalProfile__ai-warning-checkbox-notice {
        @apply text-neutral-01-400;
      }
    }
    &-required {
      @apply text-status-03-300 absolute ml-1;
      vertical-align: super;
    }
    &-text {
      @apply text-body-02 -mt-0.5;
    }
    &-notice {
      @apply block text-caption-01 text-neutral-01-400;
    }
  }
  &__push-checkbox {
    @apply mt-4;
    &-text {
      @apply text-body-02 -mt-0.5;
    }
  }
  &__birthday-notice {
    @apply text-neutral-01-400 text-caption-02 mt-1 absolute;
  }
  &__counter {
    @apply text-caption-02 text-neutral-01-400 text-right mt-1;
  }
  &__scope {
    @apply text-caption-02 text-neutral-01-400 mt-2;
  }
}
</style>
